import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { ELanguage, ELanguageKeysType } from "../../../models/enums/language";
import { ERole } from "../../../models/enums/role";
import { IUserGroup } from "../../../models/propertyGroup";
import IUser from "../../../models/user";
import { IUserState, usableRoles } from "../../../pages/table/user/UserAddPage";
import { IUpdatedUser } from "../../../pages/table/user/UserEditPage";
import UserService from "../../../services/user/UserService";
import { validEmailRegex, validUserCodeRegex } from "../../../utils/regex";
import { EntryAndLineButtonWrapper, EntryField, EntryTitle, EntryWrapper, LineButtonWrapper } from "../../customStyles/form";
import EditOnly from "../../permissions/EditOnly";

export function updateUserState<T extends IUserState | IUpdatedUser>(
    setUserState: React.Dispatch<React.SetStateAction<T>>,
    field: keyof T,
    value: T[keyof T]
) {
    setUserState((prevState: T) => ({
        ...prevState,
        [field]: value,
    }));

    if (field === "firstName" || field === "lastName") {
        setUserState((prevState: T) => ({
            ...prevState,
            lastName: prevState.lastName?.trim() || undefined,
            firstName: prevState.firstName?.trim() || undefined,
            name: `${prevState.firstName?.trim()} ${prevState.lastName?.trim() || ""}`.trim(),
        }));
    }
}

interface UserFormFieldsProps {
    userState: IUserState | IUpdatedUser;
    setUserState: React.Dispatch<React.SetStateAction<IUserState | IUpdatedUser>>;
    managers: IUser[];
    propertyGroups: IUserGroup[];
}

const CODE_GENERATION_COOLDOWN = 10; // in seconds

const UserFormFields: FC<UserFormFieldsProps> = ({
    userState,
    setUserState,
    managers,
    propertyGroups
}) => {
    const { t } = useTranslation();
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    // SSO users authenticate against Microsoft and never use the field-employee mobile flow, so Employee
    // isn't offered for them — which also keeps the Manager field (only shown for Employee) out of play.
    const selectableRoles = userState.microsoftId
        ? usableRoles.filter((role) => role !== ERole.Employee)
        : usableRoles;

    const generateUserCode = async () => {
        setIsGeneratingCode(true);
        setCooldown(CODE_GENERATION_COOLDOWN);

        try {
            const code = await UserService.GenerateUserCode();
            updateUserState(setUserState, "code", code.toString());
            updateUserState(setUserState, "isManualCode", false);
        } catch (error) {
        }

        const interval = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setIsGeneratingCode(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };


    return (
        <>
            <EntryAndLineButtonWrapper>
                <EntryWrapper>
                    <EntryTitle>
                        {t("header.code")} *
                        <Tooltip
                            title={t("table.user.codeTooltip")}
                            sx={{ marginLeft: "0.5em" }}
                        >
                            <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                        </Tooltip>
                    </EntryTitle>
                    <EntryField>
                        <TextField
                            inputProps={{
                                maxLength: 6,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                style: {
                                    fontFamily: "monospace",
                                    letterSpacing: "0.3em",
                                },
                            }}
                            error={
                                !!userState.code &&
                                !validUserCodeRegex.test(userState.code)
                            }
                            placeholder="000000"
                            value={userState.code}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "");
                                updateUserState(setUserState, "code", raw);
                                updateUserState(setUserState, "isManualCode", true);
                            }}
                            fullWidth
                        />
                    </EntryField>

                </EntryWrapper>
                <LineButtonWrapper>
                    <EditOnly>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={generateUserCode}
                            disabled={isGeneratingCode}
                            sx={{ marginLeft: "1em" }}
                            size="small"
                        >
                            {isGeneratingCode
                                ? `${t("table.user.generateCode")} (${cooldown}s)`
                                : t("table.user.generateCode")}
                        </Button>
                    </EditOnly>

                </LineButtonWrapper>
            </EntryAndLineButtonWrapper>


            <EntryWrapper>
                <EntryTitle>{t("table.user.role")} *</EntryTitle>
                <EntryField>
                    <Select
                        required={true}
                        value={userState.role}
                        onChange={(e) =>
                            updateUserState(
                                setUserState,
                                "role",
                                Number(e.target.value)
                            )
                        }
                        sx={{ width: "100%", height: "2em" }}
                        MenuProps={{
                            style: { overflowY: "visible" },
                        }}
                        renderValue={(selectedRole) =>
                            t(`table.user.${ERole[selectedRole as ERole]}`)
                        }
                    >
                        {selectableRoles.map((role) => (
                            <MenuItem key={role} value={role} sx={{ whiteSpace: "normal", alignItems: "flex-start" }}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {t(`table.user.${ERole[role]}`)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {t(`table.user.${ERole[role]}Description`)}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.user.firstName")} *</EntryTitle>
                <EntryField>
                    <TextField
                        disabled={!!userState.microsoftId}
                        required={true}
                        style={{
                            textAlign: "left",
                            width: "100%",
                        }}
                        value={userState.firstName}
                        onChange={(e) =>
                            updateUserState(
                                setUserState,
                                "firstName",
                                e.target.value
                            )
                        }
                    />
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.user.lastName")} *</EntryTitle>
                <EntryField>
                    <TextField
                        disabled={!!userState.microsoftId}
                        required={true}
                        style={{
                            textAlign: "left",
                            width: "100%",
                        }}
                        value={userState.lastName}
                        onChange={(e) =>
                            updateUserState(
                                setUserState,
                                "lastName",
                                e.target.value
                            )
                        }
                    />
                </EntryField>
            </EntryWrapper>

            {(userState.role === ERole.Admin || userState.role === ERole.Manager || userState.role === ERole.Supervisor || userState.role === ERole.Client || userState.role === ERole.LimitedClient) && (
                <EntryWrapper>
                    <EntryTitle>{t("table.user.email")} *</EntryTitle>
                    <EntryField>
                        <TextField
                            disabled={!!userState.microsoftId}
                            required={true}
                            style={{
                                textAlign: "left",
                                width: "100%",
                            }}
                            type="email"
                            error={
                                !!userState.email &&
                                !validEmailRegex.test(userState.email!)
                            }
                            value={userState.email}
                            onChange={(e) =>
                                updateUserState(
                                    setUserState,
                                    "email",
                                    e.target.value
                                )
                            }
                        />
                    </EntryField>
                </EntryWrapper>
            )}

            {userState.role === ERole.Employee && (
                <EntryWrapper>
                    <EntryTitle>{t("table.user.Manager")} *</EntryTitle>
                    <EntryField>
                        <Select
                            disabled={!!userState.microsoftId}
                            required={true}
                            value={userState.managerId}
                            onChange={(e) =>
                                updateUserState(
                                    setUserState,
                                    "managerId",
                                    Number(e.target.value)
                                )
                            }
                            sx={{ width: "100%", height: "2em" }}
                            MenuProps={{
                                style: { overflowY: "visible" },
                            }}
                        >
                            {managers.map(
                                (manager, index) =>
                                    manager.name && (
                                        <MenuItem
                                            key={index}
                                            value={manager.id}
                                        >
                                            {manager.name}
                                        </MenuItem>
                                    )
                            )}
                        </Select>
                    </EntryField>
                </EntryWrapper>
            )}

            <EntryWrapper>
                <EntryTitle>{t("table.property.propertyGroups")} </EntryTitle>
                <EntryField>
                    <Select
                        labelId="property-select-label"
                        multiple
                        value={userState.propertyGroupIds ?? []}
                        sx={{ width: "100%", height: "2em" }}
                        MenuProps={{
                            style: { overflowY: "visible" },
                        }}
                        onChange={(e) =>
                            updateUserState(
                                setUserState,
                                "propertyGroupIds",
                                e.target.value as number[]
                            )
                        }
                        renderValue={(selected) => {
                            if (selected.length === 0) return "";

                            return (selected)
                                .map((id) => {
                                    const group = propertyGroups.find(
                                        (b) => b.propertyGroupId === id
                                    );
                                    return group ? group.name : "";
                                })
                                .filter((name) => name !== "")
                                .join(", ");
                        }}
                    >

                        {propertyGroups.map((propertyGroup) => (
                            <MenuItem key={propertyGroup.propertyGroupId} value={propertyGroup.propertyGroupId}>
                                {propertyGroup.name}
                            </MenuItem>
                        ))}
                    </Select>
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.user.language")} *</EntryTitle>
                <EntryField>
                    <Select
                        required={true}
                        value={userState.language}
                        onChange={(e) =>
                            updateUserState(
                                setUserState,
                                "language",
                                Number(e.target.value)
                            )
                        }
                        sx={{ width: "100%", height: "2em" }}
                        MenuProps={{
                            style: { overflowY: "visible" },
                        }}
                    >
                        {Object.keys(ELanguage)
                            .filter((key) => isNaN(Number(key)))
                            .map((language, index) => (
                                <MenuItem
                                    key={index}
                                    value={
                                        ELanguage[language as ELanguageKeysType]
                                    }
                                >
                                    {t(`header.${language.toLowerCase()}`)}
                                </MenuItem>
                            ))}
                    </Select>
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.user.acombaEmpoyeeId")}</EntryTitle>
                <EntryField>
                    <TextField
                        type="number"
                        required={false}
                        style={{
                            textAlign: "left",
                            width: "100%",
                        }}
                        value={userState.acombaEmployeeId || undefined}
                        onChange={(e) => {
                            updateUserState(
                                setUserState,
                                "acombaEmployeeId",
                                Number(e.target.value) || undefined
                            )

                            if (!Number(e.target.value)) {
                                updateUserState(
                                    setUserState,
                                    "acombaFunctionNumber",
                                    undefined
                                );
                            }
                        }}
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                </EntryField>
            </EntryWrapper>
            {!!userState.acombaEmployeeId && (
                <EntryWrapper>
                    <EntryTitle>{t("table.user.acombaFunctionNumber")}</EntryTitle>
                    <EntryField>
                        <TextField
                            type="number"
                            required={false}
                            style={{
                                textAlign: "left",
                                width: "100%",
                            }}
                            value={userState.acombaFunctionNumber || undefined}
                            onChange={(e) =>
                                updateUserState(
                                    setUserState,
                                    "acombaFunctionNumber",
                                    Number(e.target.value) || undefined
                                )
                            }
                            slotProps={{ htmlInput: { min: 1 } }}
                        />
                    </EntryField>
                </EntryWrapper>
            )}
        </>
    );
};

export default UserFormFields;
